import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { can, allowedBranches } from '../lib/perms';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, Search, Plus, Undo2, Printer, FileDown, FileSpreadsheet } from 'lucide-react';
import { printParList, qzErrorMessage } from '../lib/qzPrint';
import { AddItemsDialog } from '../components/par/AddItemsDialog';
import { ParHistory } from '../components/par/ParHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const parAt = (item, branch) => {
  const pv = item.par_values || {};
  return pv[branch.branch_id] || pv[branch.name] || 0;
};

export default function ParGrid() {
  const { user } = useAuth();
  const isAdmin = !user?.branch_ids;
  const canEditPar = can(user, 'par_grid.edit');
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [view, setView] = useState('stocked');
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async () => {
    const [i, b, s] = await Promise.all([
      axios.get(`${API}/stock-items`, { withCredentials: true }),
      axios.get(`${API}/branches`, { withCredentials: true }),
      axios.get(`${API}/suppliers`, { withCredentials: true }),
    ]);
    setItems(i.data.sort((a, c) => a.name.localeCompare(c.name)));
    setBranches(b.data);
    setSuppliers(s.data.filter((x) => !x.archived));
  }, []);

  useEffect(() => { load().catch(() => toast.error('Failed to load PAR data')); }, [load]);

  const shown = useMemo(() => allowedBranches(user, branches), [branches, user]);
  const editable = useMemo(() => (canEditPar ? shown : []), [canEditPar, shown]);

  const rows = useMemo(() => items.filter((it) => {
    if (search && !it.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (supplierId && it.supplier_id !== supplierId && !(it.suppliers || []).some((s) => s.supplier_id === supplierId)) return false;
    const stocked = shown.some((b) => parAt(it, b) > 0);
    if (view === 'stocked') return stocked;
    if (view === 'missing') return !stocked;
    return true;
  }), [items, search, supplierId, view, shown]);

  const keyOf = (it, b) => `${it.stock_item_id}|${b.branch_id}`;
  const valueOf = (it, b) => (edits[keyOf(it, b)] !== undefined ? edits[keyOf(it, b)] : String(parAt(it, b) || ''));
  const dirtyCount = Object.keys(edits).length;

  const setCell = (it, b, v) => {
    const k = keyOf(it, b);
    setEdits((e) => {
      const next = { ...e };
      if ((parseFloat(v) || 0) === (parAt(it, b) || 0)) delete next[k]; else next[k] = v;
      return next;
    });
  };

  const saveChanges = async (changes) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/par-values/bulk`, { changes }, { withCredentials: true });
      toast.success(r.data.message);
      setEdits({});
      setHistoryKey((k) => k + 1);
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save PAR values');
      return false;
    } finally { setSaving(false); }
  };

  const [printBranch, setPrintBranch] = useState('');
  const printBranchId = printBranch || shown[0]?.branch_id || '';

  const doPrint = async () => {
    try {
      const p = await printParList(printBranchId, supplierId);
      toast.success(`PAR list sent to ${p.name || p.ip_address}`);
    } catch (e) { toast.error(qzErrorMessage(e)); }
  };

  const doExport = async (format) => {
    try {
      const params = { format, ...(supplierId ? { supplier_id: supplierId } : {}) };
      const res = await axios.get(`${API}/par-values/export`, { withCredentials: true, params, responseType: 'blob' });
      const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `PAR_Grid.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error('Export failed'); }
  };

  const saveAll = () => saveChanges(Object.entries(edits).map(([k, v]) => {
    const [stock_item_id, branch_id] = k.split('|');
    return { stock_item_id, branch_id, par: parseFloat(v) || 0 };
  }));

  if (user?.branch_ids && user.branch_ids.length === 0) {
    return <p className="text-muted-foreground" data-testid="par-no-branch">Your user has no branch assigned. Ask an admin to set your branch in Settings &gt; Users.</p>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="par-grid-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>PAR Grid</h1>
        <p className="text-muted-foreground mt-1">
          {isAdmin ? 'Set PAR values for every item across all branches.' : `PAR values for ${shown.map((b) => b.name).join(', ')}.`} Items with a PAR above 0 appear in that branch's stock take.
        </p>
      </div>

      <Tabs defaultValue="grid" className="space-y-4">
        <TabsList>
          <TabsTrigger value="grid" data-testid="par-tab-grid">Grid</TabsTrigger>
          <TabsTrigger value="history" data-testid="par-tab-history">Change History</TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input className="pl-8 w-56" placeholder="Search items" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="par-search" />
            </div>
            <Select value={supplierId || 'all'} onValueChange={(v) => setSupplierId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-52" data-testid="par-supplier-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-56" data-testid="par-view-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stocked">{isAdmin ? 'Items with a PAR somewhere' : 'Items at my branch'}</SelectItem>
                <SelectItem value="missing">{isAdmin ? 'Items with no PAR anywhere' : 'Items not at my branch'}</SelectItem>
                <SelectItem value="all">All items</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex gap-2">
              {canEditPar && <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="par-add-items-btn">
                <Plus className="w-4 h-4 mr-2" />{isAdmin ? 'Add items to a branch' : 'Add items to my branch'}
              </Button>}
              {dirtyCount > 0 && (
                <Button variant="ghost" onClick={() => setEdits({})} data-testid="par-discard-btn"><Undo2 className="w-4 h-4 mr-2" />Discard</Button>
              )}
              <Button onClick={saveAll} disabled={!dirtyCount || saving} data-testid="par-save-all-btn">
                <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : `Save all${dirtyCount ? ` (${dirtyCount})` : ''}`}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center justify-end" data-testid="par-output-bar">
            <Button variant="outline" size="sm" onClick={() => doExport('xlsx')} data-testid="par-export-xlsx-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Excel</Button>
            <Button variant="outline" size="sm" onClick={() => doExport('pdf')} data-testid="par-export-pdf-btn"><FileDown className="w-4 h-4 mr-1.5" />PDF</Button>
            {isAdmin && (
              <Select value={printBranchId} onValueChange={setPrintBranch}>
                <SelectTrigger className="w-48 h-9" data-testid="par-print-branch-select"><SelectValue placeholder="Branch to print" /></SelectTrigger>
                <SelectContent>{shown.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" onClick={doPrint} disabled={!printBranchId} data-testid="par-print-btn"><Printer className="w-4 h-4 mr-1.5" />Print PAR list</Button>
          </div>

          <Card>
            <CardContent className="p-0 overflow-auto max-h-[65vh]">
              <table className="w-full text-sm" data-testid="par-grid-table">
                <thead className="sticky top-0 z-10 bg-card border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium sticky left-0 bg-card min-w-[240px]">Item</th>
                    {shown.map((b) => <th key={b.branch_id} className="px-3 py-3 font-medium text-center min-w-[110px]">{b.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((it) => (
                    <tr key={it.stock_item_id} className="border-b hover:bg-muted/40" data-testid={`par-row-${it.stock_item_id}`}>
                      <td className="px-4 py-2 sticky left-0 bg-card">
                        <p className="font-medium">{it.name}</p>
                        <p className="text-xs text-muted-foreground">{it.supplier_name || '—'} · {it.unit}</p>
                      </td>
                      {shown.map((b) => {
                        const dirty = edits[keyOf(it, b)] !== undefined;
                        const canEdit = editable.some((e) => e.branch_id === b.branch_id);
                        return (
                          <td key={b.branch_id} className="px-3 py-1.5 text-center">
                            {canEdit ? (
                              <input type="number" min="0" step="0.01" inputMode="decimal" value={valueOf(it, b)} placeholder="0"
                                onChange={(e) => setCell(it, b, e.target.value)}
                                className={`w-20 h-8 rounded-md border px-2 text-center bg-background focus:outline-none focus:ring-2 focus:ring-ring ${dirty ? 'border-amber-500 bg-amber-50' : 'border-input'}`}
                                data-testid={`par-cell-${it.stock_item_id}-${b.branch_id}`} />
                            ) : <span>{parAt(it, b) || '—'}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && <p className="text-center text-muted-foreground py-10" data-testid="par-empty">No items match these filters.</p>}
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">{rows.length} items shown {dirtyCount > 0 && <Badge variant="outline" className="ml-2">{dirtyCount} unsaved</Badge>}</p>
        </TabsContent>

        <TabsContent value="history">
          <ParHistory branches={shown} isAdmin={isAdmin} refreshKey={historyKey} />
        </TabsContent>
      </Tabs>

      <AddItemsDialog open={addOpen} onOpenChange={setAddOpen} items={items} branches={editable}
        onAdd={async (changes) => { if (await saveChanges(changes)) setAddOpen(false); }} saving={saving} />
    </div>
  );
}
